import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import { bloomberg } from '../theme/bloombergTheme';
import api from '../services/api';
import InfoPopover from './InfoPopover';

interface MacroEvent {
  date: string;
  time: string;
  all_day: boolean;
  currency: string;
  country: string;
  impact: 'high' | 'medium' | 'low' | 'holiday';
  event: string;
  actual: string;
  forecast: string;
  previous: string;
}

const IMPACT_COLORS: Record<string, { color: string; label: string }> = {
  high: { color: '#ef4444', label: 'HIGH IMPACT' },
  medium: { color: '#f59e0b', label: 'MEDIUM IMPACT' },
  low: { color: bloomberg.text.muted, label: 'LOW IMPACT' },
  holiday: { color: '#3b82f6', label: 'HOLIDAY' },
};

const CURRENCY_FLAGS: Record<string, string> = {
  USD: '\u{1F1FA}\u{1F1F8}',
  EUR: '\u{1F1EA}\u{1F1FA}',
  GBP: '\u{1F1EC}\u{1F1E7}',
  JPY: '\u{1F1EF}\u{1F1F5}',
  AUD: '\u{1F1E6}\u{1F1FA}',
  NZD: '\u{1F1F3}\u{1F1FF}',
  CAD: '\u{1F1E8}\u{1F1E6}',
  CHF: '\u{1F1E8}\u{1F1ED}',
  CNY: '\u{1F1E8}\u{1F1F3}',
};

const MacroCalendar: React.FC = () => {
  const [events, setEvents] = useState<MacroEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [impactFilter, setImpactFilter] = useState<string>('high');
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);

  // Hover-to-preview, click-to-pin detail popover
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const [pinned, setPinned] = useState(false);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/api/market/macro-calendar`);
      setEvents(data.events || []);
      setLastUpdated(data.last_updated);
    } catch (err) {
      console.error('Error fetching macro calendar:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, []);

  const filteredEvents = impactFilter === 'all'
    ? events
    : events.filter((e) => e.impact === impactFilter);

  const eventsByDate = filteredEvents.reduce<Record<string, MacroEvent[]>>((acc, ev) => {
    const key = ev.date || 'Unknown';
    if (!acc[key]) acc[key] = [];
    acc[key].push(ev);
    return acc;
  }, {});

  const sortedDates = Object.keys(eventsByDate).sort();

  const formatDateHeader = (dateStr: string) => {
    if (!dateStr || dateStr === 'Unknown') return 'Unknown Date';
    try {
      const d = new Date(dateStr + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(tomorrow.getDate() + 1);

      if (d.getTime() === today.getTime()) return 'Today';
      if (d.getTime() === tomorrow.getTime()) return 'Tomorrow';
      return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const isToday = (dateStr: string) => {
    try {
      const d = new Date(dateStr + 'T00:00:00');
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return d.getTime() === today.getTime();
    } catch {
      return false;
    }
  };

  const handleEnter = (e: React.MouseEvent<HTMLElement>, idx: number) => {
    if (pinned) return;
    setAnchorEl(e.currentTarget);
    setActiveIdx(idx);
  };
  const handleLeave = () => {
    if (!pinned) {
      setAnchorEl(null);
      setActiveIdx(null);
    }
  };
  const handleClick = (e: React.MouseEvent<HTMLElement>, idx: number) => {
    if (pinned && activeIdx === idx) {
      setPinned(false);
      setAnchorEl(null);
      setActiveIdx(null);
    } else {
      setPinned(true);
      setAnchorEl(e.currentTarget);
      setActiveIdx(idx);
    }
  };
  const handleClose = () => {
    setPinned(false);
    setAnchorEl(null);
    setActiveIdx(null);
  };

  const activeEvent = activeIdx != null ? filteredEvents[activeIdx] : null;
  let rowCursor = -1;

  return (
    <Paper elevation={2} sx={{ p: 2.5, mb: 3, height: 600, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PublicIcon sx={{ color: bloomberg.accent.orange, fontSize: 20 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: bloomberg.accent.orange }}>
          Macro Events
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
          {lastUpdated && (
            <Typography variant="caption" sx={{ color: bloomberg.text.muted, mr: 1 }}>
              Updated {new Date(lastUpdated).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
            </Typography>
          )}
          <ToggleButtonGroup
            value={impactFilter}
            exclusive
            onChange={(_, val) => val && setImpactFilter(val)}
            size="small"
          >
            <ToggleButton value="high" sx={{ px: 1, py: 0.3, fontSize: bloomberg.fontSize.micro }}>
              High
            </ToggleButton>
            <ToggleButton value="medium" sx={{ px: 1, py: 0.3, fontSize: bloomberg.fontSize.micro }}>
              Med
            </ToggleButton>
            <ToggleButton value="all" sx={{ px: 1, py: 0.3, fontSize: bloomberg.fontSize.micro }}>
              All
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[...Array(8)].map((_, i) => <Skeleton key={i} variant="rounded" height={26} />)}
        </Box>
      ) : filteredEvents.length === 0 ? (
        <Typography color="text.secondary" variant="body2" sx={{ py: 2, textAlign: 'center' }}>
          No macro events found
        </Typography>
      ) : (
        <Box>
          {sortedDates.map((date) => (
            <Box key={date} sx={{ mb: 0.5 }}>
              <Box sx={{
                display: 'flex', alignItems: 'center', gap: 1, py: 0.5, px: 0.5,
                bgcolor: isToday(date) ? bloomberg.accent.orangeDim : 'transparent',
                borderLeft: `3px solid ${isToday(date) ? bloomberg.accent.orange : bloomberg.border.primary}`,
                pl: 1,
              }}>
                <Typography sx={{ fontSize: bloomberg.fontSize.caption, fontWeight: 700, color: isToday(date) ? bloomberg.accent.orange : bloomberg.text.primary, letterSpacing: '0.03em' }}>
                  {formatDateHeader(date)}
                </Typography>
              </Box>
              {eventsByDate[date].map((ev) => {
                rowCursor += 1;
                const idx = rowCursor;
                const impactStyle = IMPACT_COLORS[ev.impact] || IMPACT_COLORS.low;
                const flag = CURRENCY_FLAGS[ev.currency] || '';
                return (
                  <Box
                    key={idx}
                    onMouseEnter={(e) => handleEnter(e, idx)}
                    onMouseLeave={handleLeave}
                    onClick={(e) => handleClick(e, idx)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.25, py: 0.85, px: 1,
                      borderBottom: `1px solid ${bloomberg.border.primary}`,
                      cursor: 'pointer',
                      '&:hover': { bgcolor: bloomberg.bg.hover },
                    }}
                  >
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: impactStyle.color, flexShrink: 0 }} />
                    <Typography sx={{ fontFamily: bloomberg.font.mono, fontSize: bloomberg.fontSize.caption, color: bloomberg.text.muted, width: 68, flexShrink: 0 }}>
                      {ev.all_day ? (ev.time === 'Tentative' ? 'TBD' : 'All Day') : ev.time}
                    </Typography>
                    {flag && <span style={{ fontSize: 13, flexShrink: 0 }}>{flag}</span>}
                    <Typography sx={{
                      fontSize: bloomberg.fontSize.bodyDense, color: bloomberg.text.primary, flex: 1,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {ev.event}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          ))}
        </Box>
      )}

      <InfoPopover anchorEl={anchorEl} onClose={handleClose}>
        {activeEvent && (
          <Box onMouseEnter={() => setAnchorEl(anchorEl)} onMouseLeave={handleLeave}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              {CURRENCY_FLAGS[activeEvent.currency] && (
                <span style={{ fontSize: 15 }}>{CURRENCY_FLAGS[activeEvent.currency]}</span>
              )}
              <Typography sx={{ fontSize: bloomberg.fontSize.bodyDense, fontWeight: 700, color: bloomberg.text.primary }}>
                {activeEvent.event}
              </Typography>
            </Box>
            <Typography sx={{ fontSize: bloomberg.fontSize.micro, fontWeight: 700, color: (IMPACT_COLORS[activeEvent.impact] || IMPACT_COLORS.low).color, letterSpacing: '0.05em', mb: 1 }}>
              {(IMPACT_COLORS[activeEvent.impact] || IMPACT_COLORS.low).label} &middot; {activeEvent.currency}
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
              {[['Actual', activeEvent.actual], ['Forecast', activeEvent.forecast], ['Previous', activeEvent.previous]].map(([label, val]) => (
                <Box key={label}>
                  <Typography sx={{ fontSize: bloomberg.fontSize.micro, color: bloomberg.text.muted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</Typography>
                  <Typography sx={{ fontSize: bloomberg.fontSize.bodyDense, fontWeight: 600, fontFamily: bloomberg.font.mono, color: bloomberg.text.primary }}>{val || '—'}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </InfoPopover>
    </Paper>
  );
};

export default MacroCalendar;
